'use client';

import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { cn } from '@/utils/сn';
import { useState } from 'react';
import { CALENDAR_LEGEND } from '@/app/(main)/calendar/consts/calendar-panel';
import Checkbox from '@/app/components/Checkbox';
import type { CalendarVisibilityState } from '@/types/calendarTypes';
import ChevronDownIcon from '/public/icons/chevron-down.svg';
import LabelIcon from '/public/icons/label.svg';

type CalendarMobileFiltersProps = {
  visibility: CalendarVisibilityState;
  onVisibilityChange: (next: CalendarVisibilityState) => void;
};

export function CalendarMobileFilters({ visibility, onVisibilityChange }: CalendarMobileFiltersProps) {
  const [isLegendOpen, setIsLegendOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isLegendOpen,
    onOpenChange: setIsLegendOpen,
    placement: 'bottom-end',
    strategy: 'fixed',
    middleware: [offset(10), flip(), shift({ padding: 16 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const toggle = (key: keyof CalendarVisibilityState) => {
    onVisibilityChange({ ...visibility, [key]: !visibility[key] });
  };

  return (
    <div className="relative flex items-center justify-between gap-2 px-4 py-3 md:hidden">
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
        <label className="flex cursor-pointer items-center gap-1.5 text-body-small text-grey-800">
          <Checkbox size="small" checked={visibility.events} onChange={() => toggle('events')} />
          Events
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-body-small text-grey-800">
          <Checkbox size="small" checked={visibility.tasks} onChange={() => toggle('tasks')} />
          Tasks
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-body-small text-grey-800">
          <Checkbox size="small" checked={visibility.birthdays} onChange={() => toggle('birthdays')} />
          Birthdays
        </label>
      </div>

      <button
        ref={refs.setReference}
        type="button"
        className="flex shrink-0 items-center gap-0.5 rounded p-1 text-blue-600 hover:bg-blue-50"
        aria-label="Event color legend"
        aria-expanded={isLegendOpen}
        {...getReferenceProps()}
      >
        <LabelIcon className="h-4 w-4 shrink-0" aria-hidden />
        <ChevronDownIcon className={cn('h-3 w-3 shrink-0 fill-current text-blue-300 transition-transform duration-200', { 'rotate-180': isLegendOpen })} aria-hidden />
      </button>

      {isLegendOpen ? (
        <div
          ref={refs.setFloating}
          style={{ ...floatingStyles, zIndex: 50 }}
          className="outline-none"
          {...getFloatingProps()}
        >
          <div className="relative rounded-md border border-grey-300 bg-white p-4 shadow-card">
            <ul className="relative flex flex-col gap-3">
              {CALENDAR_LEGEND.map((item) => (
                <li key={item.label} className="flex items-center gap-2">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${item.className}`} aria-hidden />
                  <span className="text-xs font-normal font-inter text-grey-700">{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}
